import React, { useEffect, useState } from "react";

import { BrowserRouter, Route, Routes } from "react-router-dom";

import AuthLayout from "./routes/middleware/AuthLayout";
import NonAuthLayout from "./routes/middleware/NonAuthLayout";
import UserLayout from "./routes/middleware/UserLayout";

import {
  authRoutes,
  nonAuthRoutes,
  userRoutes,
} from "./routes/AllRoutes";

import AuthProvider from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

import { Toaster } from "react-hot-toast";

import PageLoader from "./components/common/PageLoader";
import FloatingContact from "./components/common/FloatingContact";
import ServicesAssistant from "./ServicesAssistant/ServicesAssistant";

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  /* ============================================================
     PAGE LOADER
  ============================================================ */

  useEffect(() => {
    const MIN_LOADER_MS = 2000;
    const MAX_LOADER_MS = 8000;

    let loadFired = document.readyState === "complete";
    let minElapsed = false;

    const hideIfReady = () => {
      if (loadFired && minElapsed) {
        setIsLoading(false);
      }
    };

    const onWindowLoad = () => {
      loadFired = true;
      hideIfReady();
    };

    window.addEventListener("load", onWindowLoad);

    const minTimer = setTimeout(() => {
      minElapsed = true;
      hideIfReady();
    }, MIN_LOADER_MS);

    const maxTimer = setTimeout(() => {
      setIsLoading(false);
    }, MAX_LOADER_MS);

    return () => {
      window.removeEventListener("load", onWindowLoad);
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
  }, []);

  /* ============================================================
     AUTH ROUTES
  ============================================================ */

  const renderAuthRoutes = (routes) => {
    return routes.map((route, idx) => (
      <Route
        key={route.path || idx}
        path={route.path}
        element={
          <AuthLayout>
            {route.component}
          </AuthLayout>
        }
      />
    ));
  };

  /* ============================================================
     NON AUTH ROUTES
  ============================================================ */

  const renderNonAuthRoutes = (routes) => {
    return routes.map((route, idx) => (
      <Route
        key={route.path || idx}
        path={route.path}
        element={
          <NonAuthLayout>
            {route.component}
          </NonAuthLayout>
        }
      />
    ));
  };

  /* ============================================================
     USER ROUTES
  ============================================================ */

  const renderUserRoutes = (routes) => {
    return routes.map((route, idx) => (
      <Route
        key={route.path || idx}
        path={route.path}
        element={
          <UserLayout>
            {route.component}
          </UserLayout>
        }
      />
    ));
  };

  /* ============================================================
     CHATBOT
  ============================================================ */

  const handleOpenChatbot = () => {
    setIsChatbotOpen(true);
  };

  const handleCloseChatbot = () => {
    setIsChatbotOpen(false);
  };

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="min-h-screen theme-bg-primary theme-text-primary">
            
            {/* ==================================================
                PAGE LOADER
            ================================================== */}

            {isLoading && <PageLoader />}

            {/* ==================================================
                ROUTES
            ================================================== */}

            <Routes>
              {renderNonAuthRoutes(nonAuthRoutes)}
              {renderAuthRoutes(authRoutes)}
              {renderUserRoutes(userRoutes)}
            </Routes>

            {/* ==================================================
                FLOATING CONTACT
            ================================================== */}

            <FloatingContact />

            {/* ==================================================
                AI ASSISTANT
            ================================================== */}

            <ServicesAssistant
              isOpen={isChatbotOpen}
              onClose={handleCloseChatbot}
            />

            {/* ==================================================
                AI ASSISTANT OPEN BUTTON
            ================================================== */}

            {!isChatbotOpen && (
              <button
                type="button"
                onClick={handleOpenChatbot}
                aria-label="Open AI Assistant"
                aria-expanded={isChatbotOpen}
                title="Chat with AI Assistant"
                className="
                  group
                  fixed
                  z-[9999]
                  bottom-4
                  right-3
                  sm:bottom-5
                  sm:right-4
                  md:bottom-7
                  md:right-5
                  lg:bottom-8
                  lg:right-6
                  xl:bottom-10
                  xl:right-7
                  flex
                  shrink-0
                  items-center
                  justify-center
                  h-11
                  w-11
                  sm:h-12
                  sm:w-12
                  md:h-[50px]
                  md:w-[50px]
                  lg:h-[54px]
                  lg:w-[54px]
                  xl:h-[56px]
                  xl:w-[56px]
                  rounded-full
                  border
                  border-white/10
                  bg-gradient-to-br
                  from-[#F7931E]
                  to-[#E76A17]
                  shadow-[0_10px_30px_rgba(247,147,30,0.28)]
                  outline-none
                  transition-all
                  duration-300
                  ease-out
                  hover:scale-[1.07]
                  hover:shadow-[0_14px_40px_rgba(247,147,30,0.40)]
                  active:scale-[0.93]
                  focus-visible:ring-2
                  focus-visible:ring-[#F7931E]/60
                  focus-visible:ring-offset-2
                  focus-visible:ring-offset-black
                "
              >
                {/* =================================================
                    INNER HIGHLIGHT
                ================================================== */}

                <span
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    rounded-full
                    bg-white/10
                    opacity-0
                    transition-opacity
                    duration-300
                    group-hover:opacity-100
                  "
                />

                {/* =================================================
                    SUBTLE INNER BORDER
                ================================================== */}

                <span
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute
                    inset-[1px]
                    rounded-full
                    border
                    border-white/[0.12]
                  "
                />

                {/* =================================================
                    AI ROBOT
                ================================================== */}

                <span
                  className="
                    relative
                    z-10
                    flex
                    h-full
                    w-full
                    shrink-0
                    items-center
                    justify-center
                    select-none
                    leading-none
                    text-[21px]
                    sm:text-[22px]
                    md:text-[24px]
                    lg:text-[25px]
                    xl:text-[27px]
                    transition-transform
                    duration-300
                    group-hover:scale-110
                  "
                  role="img"
                  aria-label="AI Assistant"
                >
                  🤖
                </span>
              </button>
            )}

            {/* =====================================================
                TOASTER
            ====================================================== */}

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
              }}
            />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App; 